/* Estado do saldo pré-pago, num lugar só.
 *
 * Duas armadilhas justificam este módulo existir em vez de a comparação ficar
 * solta em cada tela:
 *
 * 1. O saldo NÃO chega como número. A coluna é `DECIMAL(14,2)` e o driver do
 *    MySQL devolve `"0.00"`, não `0`. Por isso o Dashboard comparava
 *    `credit === 0` e o aviso de saldo esgotado nunca aparecia: o cliente já
 *    estava barrado no backend (HTTP 402) e a tela dizia apenas "saldo
 *    acabando". Aqui o valor é convertido uma vez, e as comparações são
 *    numéricas.
 *
 * 2. O limite tem que ser o mesmo em todo lugar. Sidebar e Dashboard mostravam
 *    réguas diferentes (10 num, nada no outro); duas fontes da mesma verdade
 *    viram duas mensagens contraditórias na mesma sessão.
 *
 * Saldo ausente (null/undefined/não numérico) NÃO alarma: durante o carregamento
 * o campo pode não ter chegado ainda, e um "sem saldo" falso é pior que um aviso
 * atrasado. Quem é pós-pago não passa por aqui — não tem saldo a zerar.
 */

/* Abaixo disto o saldo já é curto o bastante para uma consulta comum ser
   recusada por falta de crédito. */
export const LIMITE_SALDO_BAIXO = 5;

export const CREDIT_STATUS = {
  OK: "ok",
  BAIXO: "baixo",
  ZERADO: "zerado",
};

/** Saldo como número, ou `null` quando não há valor utilizável. */
export const parseCredit = (credit) => {
  if (credit === null || credit === undefined || credit === "") return null;
  const numero = Number(credit);
  return Number.isFinite(numero) ? numero : null;
};

/**
 * `zerado` cobre o negativo também: saldo abaixo de zero acontece (o débito em
 * cascata não trava no zero) e é, para o cliente, a mesma situação de bloqueio.
 */
export const getCreditStatus = (credit) => {
  const saldo = parseCredit(credit);
  if (saldo === null) return CREDIT_STATUS.OK;
  if (saldo <= 0) return CREDIT_STATUS.ZERADO;
  if (saldo < LIMITE_SALDO_BAIXO) return CREDIT_STATUS.BAIXO;
  return CREDIT_STATUS.OK;
};

export const isCreditLow = (credit) =>
  getCreditStatus(credit) !== CREDIT_STATUS.OK;

/* Tom do aviso. Os componentes mapeiam tom -> cores, em vez de cada um re-deduzir
   o visual a partir do estado: são quatro telas e seis propriedades de cor cada,
   e derivar isso em todas dava a mesma cadeia de ternários repetida. */
export const CREDIT_TONES = {
  CRITICO: "critico", // acabou, e o cliente sabe o que é recarga: alarme
  ATENCAO: "atencao", // está acabando
  CONVITE: "convite", // nunca recarregou: primeira recarga, não alarme
};

/* Texto dos avisos, num lugar só, para as telas não escreverem versões
   diferentes do mesmo recado.
 *
 * `mensagemRepresentante` existe porque a consequência é outra e bem maior: o
 * saldo do representante é debitado a cada consulta da sub-rede dele, e o
 * pré-check da cascata barra o cliente com 402 quando esse saldo não cobre o
 * preço — em qualquer profundidade, e mesmo que o cliente seja pós-pago. Para
 * um representante, ficar sem saldo não atrapalha o trabalho dele —
 * interrompe o trabalho dos clientes dele. O aviso tem que dizer isso. */
const TEXTOS = {
  [CREDIT_STATUS.BAIXO]: {
    tone: CREDIT_TONES.ATENCAO,
    titulo: "Saldo baixo",
    curto: "Seu saldo está acabando",
    mensagem:
      "Recarregue para não ter consultas recusadas por falta de crédito.",
    mensagemRepresentante:
      "Recarregue para não bloquear as consultas dos seus clientes.",
    acao: "Recarregar",
  },
  [CREDIT_STATUS.ZERADO]: {
    tone: CREDIT_TONES.CRITICO,
    titulo: "Sem saldo",
    curto: "Seu saldo acabou",
    mensagem: "Suas consultas ficam bloqueadas até a próxima recarga.",
    mensagemRepresentante:
      "As consultas dos seus clientes estão bloqueadas até a próxima recarga.",
    acao: "Recarregar agora",
  },
};

/* Saldo zero em quem NUNCA recarregou não é uma consulta bloqueada — é uma
   conta que ainda não começou. Dizer "suas consultas estão bloqueadas" para
   quem acabou de entrar soa como defeito do produto; o que falta ali é o
   primeiro passo. Mesmo texto, outro trabalho. */
const PRIMEIRA_RECARGA = {
  tone: CREDIT_TONES.CONVITE,
  titulo: "Comece a consultar",
  curto: "Você ainda não tem saldo",
  mensagem: "Faça sua primeira recarga e libere as consultas na hora.",
  mensagemRepresentante:
    "Faça sua primeira recarga para liberar as consultas da sua rede.",
  acao: "Fazer primeira recarga",
};

/* Mantido exportado para quem quiser o texto cru; `getCreditAlert` é o caminho
   normal, porque é ele que decide entre alarme e convite. */
export const CREDIT_ALERTS = TEXTOS;

/**
 * Alerta do saldo, ou `null` quando não há o que avisar.
 *
 * `isRepresentative` troca a mensagem pela versão que fala dos clientes.
 *
 * `hasRecharged` vem de `Company.hasRecharged` (o backend responde no
 * /user-current, e só para pré-pago). O default é `true` DE PROPÓSITO: se o
 * campo não vier — payload em cache, backend antigo, POS — o certo é manter a
 * mensagem de sempre. Tratar ausência como "nunca recarregou" convidaria para a
 * primeira recarga um cliente que recarrega há dois anos.
 */
export const getCreditAlert = (
  credit,
  { isRepresentative = false, hasRecharged = true } = {},
) => {
  const status = getCreditStatus(credit);
  if (status === CREDIT_STATUS.OK) return null;

  const base =
    status === CREDIT_STATUS.ZERADO && hasRecharged === false
      ? PRIMEIRA_RECARGA
      : TEXTOS[status];

  return {
    ...base,
    mensagem: isRepresentative ? base.mensagemRepresentante : base.mensagem,
  };
};
